import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './PrivacyPolicy.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShieldAlt, faUserLock, faCookieBite, faServer, 
  faFileContract, faEnvelope 
} from '@fortawesome/free-solid-svg-icons';
import { debounce } from '../utils/performance';

function PrivacyPolicy() {
  const { countryCode } = useOutletContext();
  const themeClass = `theme-${countryCode || 'usa'}`;
  const [activeSection, setActiveSection] = useState(null);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Signal to react-snap that this page has been rendered
    if (typeof window !== 'undefined' && window.document) {
      document.documentElement.setAttribute('data-prerendered', 'true');
      // Add a marker element that react-snap can detect
      const marker = document.createElement('div');
      marker.id = 'privacy-content-loaded';
      marker.style.display = 'none';
      document.body.appendChild(marker);
    }
  }, []);

  // Sections for table of contents and navigation
  const sections = [
    { id: 'intro', title: 'Introduction' },
    { id: 'definitions', title: 'Definitions' },
    // PIPEDA Principle: Accountability - Consider a dedicated section or integrate into Contact Us/About Us
    // PIPEDA Principle: Identifying Purposes - Integrate into Data Collection & Data Use
    // PIPEDA Principle: Consent - Integrate into Data Collection & Cookies
    { id: 'data-collection', title: 'Data Collection & Consent' }, // Renamed for clarity
    { id: 'cookies', title: 'Cookies & Tracking Technologies' }, // Renamed for clarity
    { id: 'data-use', title: 'How We Use Your Data' },
    // PIPEDA Principle: Limiting Use, Disclosure, and Retention
    { id: 'data-sharing', title: 'Information Sharing & Disclosure' }, // Renamed for clarity
    { id: 'data-retention', title: 'Data Retention' },
    { id: 'data-transfers', title: 'International Data Transfers' }, // Renamed for clarity
    { id: 'data-rights', title: 'Your Data Rights (PIPEDA)' }, // Emphasize PIPEDA
    // PIPEDA Principle: Safeguards
    { id: 'data-security', title: 'Security Measures' },
    // PIPEDA Principle: Openness - Achieved by a clear, comprehensive, and accessible policy
    // PIPEDA Principle: Challenging Compliance - Integrate into Contact Us / Your Data Rights
    { id: 'children', title: 'Children\'s Privacy' },
    { id: 'links', title: 'Third-Party Links' },
    { id: 'updates', title: 'Policy Updates' },
    { id: 'contact', title: 'Contact Us & Accountability' } // Renamed for clarity
  ];

  // Handle scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  // Handle scroll events to highlight active section
  useEffect(() => {
    // Create a debounced scroll handler that runs at most every 100ms
    const handleScroll = debounce(() => {
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    }, 100);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  return (
    <div className={`privacy-policy-page ${themeClass}`}>
      <Helmet>
        <title>Privacy Policy - OriginSelect</title>
        <meta name="description" content="OriginSelect Privacy Policy - Learn how we collect, use, and protect your personal information in compliance with PIPEDA and privacy regulations." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="privacy-container">
        <aside className="privacy-toc">
          <div className="toc-container">
            <h2>Contents</h2>
            <ul>
              {sections.map(section => (
                <li 
                  key={section.id}
                  className={activeSection === section.id ? 'active' : ''}
                  onClick={() => scrollToSection(section.id)}
                >
                  {section.title}
                </li>
              ))}
            </ul>
          </div>
        </aside>
        
        <div className="privacy-content">
          <div className="privacy-header">
            <FontAwesomeIcon icon={faShieldAlt} className="privacy-icon" />
            <h1>Privacy Policy</h1>
            <p className="last-updated">Last updated: May 12, 2025</p> {/* Updated Date */}
          </div>
          
          <section id="intro">
            <div className="section-heading">
              <FontAwesomeIcon icon={faUserLock} className="section-icon" />
              <h2>Introduction</h2>
            </div>
            <p>This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You. We are committed to protecting your privacy in accordance with applicable laws, including Canada's Personal Information Protection and Electronic Documents Act (PIPEDA), where applicable.</p>
            <p>We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy. If you are a resident of Canada, this policy outlines your rights under PIPEDA.</p>
          </section>
          
          <section id="definitions">
            <div className="section-heading">
              <FontAwesomeIcon icon={faFileContract} className="section-icon" />
              <h2>Definitions</h2>
            </div>
            <p>The words of which the initial letter is capitalized have meanings defined under the following conditions:</p>
            
            <div className="terms-grid">
              <div className="term-card">
                <h4>Account</h4>
                <p>A unique account created for You to access our Service or parts of our Service.</p>
              </div>
              <div className="term-card">
                <h4>Company</h4>
                <p>Refers to OriginSelect.</p>
              </div>
              <div className="term-card">
                <h4>Cookies</h4>
                <p>Small files placed on Your device by a website, containing browsing details.</p>
              </div>
              <div className="term-card">
                <h4>Country</h4>
                <p>Refers to: Washington, United States. For users in Canada, this policy also addresses requirements under Canadian privacy laws (PIPEDA).</p> {/* PIPEDA Change */}
              </div>
              <div className="term-card">
                <h4>Personal Data</h4>
                <p>Information that relates to an identified or identifiable individual.</p>
              </div>
              <div className="term-card">
                <h4>Service</h4>
                <p>Refers to the Application or the Website or both.</p>
              </div>
              <div className="term-card">
                <h4>PIPEDA</h4>
                <p>Refers to the Personal Information Protection and Electronic Documents Act, Canada's federal private sector privacy law.</p>
              </div>
            </div>
            
            {/* Add more definitions as needed */}
          </section>
          
          <section id="data-collection"> {/* Renamed to "Data Collection & Consent" in sections array */}
            <div className="section-heading">
              <FontAwesomeIcon icon={faServer} className="section-icon" />
              <h2>Data Collection & Consent</h2> {/* PIPEDA Change */}
            </div>
            <p>We collect personal information only for purposes that a reasonable person would consider appropriate in the circumstances and limit collection to what is necessary for those identified purposes. We will obtain Your consent before or at the time of collecting Your personal information, and in some cases, where appropriate, consent may be implied.</p> {/* PIPEDA Change */}
            <h3>Personal Information We Collect and Why</h3> {/* PIPEDA Change */}
            <p>While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. The purposes for collecting this information are also outlined below:</p> {/* PIPEDA Change */}
            
            <div className="data-types">
              <div className="data-type">
                <span className="data-label">Email Address</span>
                <p className="data-purpose">Purpose: To create and manage Your account, communicate with You, send newsletters (if opted-in), and provide customer support.</p> {/* PIPEDA Change */}
              </div>
              <div className="data-type">
                <span className="data-label">Name</span>
                <p className="data-purpose">Purpose: To personalize Your experience and for account identification.</p> {/* PIPEDA Change */}
              </div>
              <div className="data-type">
                <span className="data-label">Usage Data</span>
                <p className="data-purpose">Purpose: To understand how Our Service is used, improve its functionality, troubleshoot issues, and for security monitoring.</p> {/* PIPEDA Change */}
              </div>
            </div>
            
            <h3>Usage Information</h3>
            <p>Usage Data is collected automatically when using the Service. We identify the purposes for which Usage Data is collected at or before the time of collection. This data includes:</p> {/* PIPEDA Change */}
            <ul className="modern-list">
              <li>Device IP address</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent</li>
              <li>Access dates and times</li>
              <li>Device identifiers</li>
            </ul>
            <p>By using our Service, you consent to the collection of this information as described. You may withdraw your consent at any time, subject to legal or contractual restrictions and reasonable notice, by contacting us (see 'Contact Us' section).</p> {/* PIPEDA Change */}
          </section>
          
          <section id="cookies"> {/* Renamed to "Cookies & Tracking Technologies" in sections array */}
            <div className="section-heading">
              <FontAwesomeIcon icon={faCookieBite} className="section-icon" />
              <h2>Cookies & Tracking Technologies</h2> {/* PIPEDA Change */}
            </div>
            <p>We use Cookies and similar tracking technologies (like web beacons and pixels) to track activity on Our Service, store certain information, and improve and analyze Our Service. We will obtain your consent for the use of non-essential cookies. You have the right to withdraw your consent at any time.</p> {/* PIPEDA Change */}
            
            <div className="cookie-types">
              <div className="cookie-card">
                <h4>Essential Cookies</h4>
                <p><span className="tag">Session</span></p>
                <p>Required for the website to function properly, including authentication and security.</p>
              </div>
              <div className="cookie-card">
                <h4>Preference Cookies</h4>
                <p><span className="tag">Persistent</span></p>
                <p>Remember your settings and preferences for a better experience.</p>
              </div>
              <div className="cookie-card">
                <h4>Analytical Cookies</h4>
                <p><span className="tag">Persistent</span></p>
                <p>Help us understand how visitors interact with our website.</p>
              </div>
            </div>
            <h4>Managing Cookies</h4> {/* PIPEDA Change */}
            <p>You can manage your cookie preferences through your browser settings. Most browsers allow you to refuse cookies or to alert you when cookies are being sent. If you disable cookies, some parts of our Service may not function properly. We will provide a clear mechanism (e.g., a cookie banner or preference center) for you to provide or withdraw consent for non-essential cookies.</p> {/* PIPEDA Change */}
          </section>

          {/* 
            PIPEDA Considerations for other sections:
            
            <section id="data-use">
              <h2>How We Use Your Data</h2>
              <p>PIPEDA Principle: Limiting Use. Clearly state that personal information will not be used or disclosed for purposes other than those for which it was collected, except with the consent of the individual or as required by law. Detail each purpose of use.</p>
            </section>

            <section id="data-sharing"> // Renamed to "Information Sharing & Disclosure"
              <h2>Information Sharing & Disclosure</h2>
              <p>PIPEDA Principle: Limiting Disclosure. Specify with whom data might be shared (e.g., service providers, legal authorities) and under what conditions. If sharing with third-party service providers, mention that measures are taken (e.g., contracts) to ensure they provide a comparable level of protection and use the information only for the purposes for which it was transferred.</p>
            </section>

            <section id="data-retention">
              <h2>Data Retention</h2>
              <p>PIPEDA Principle: Limiting Retention. Explain that personal information will be retained only as long as necessary for the fulfillment of the identified purposes or as required by law. Outline general retention periods or criteria used to determine them. Mention secure disposal methods.</p>
            </section>

            <section id="data-transfers"> // Renamed to "International Data Transfers"
              <h2>International Data Transfers</h2>
              <p>If data is transferred outside Canada (e.g., to the US), clearly state this. Inform users that their data will be subject to the laws of the foreign jurisdiction and may be accessible to foreign governments, courts, law enforcement, and regulatory agencies. State that the company remains accountable for the information and uses contractual or other means to provide a comparable level of protection while the information is being processed by a third party.</p>
            </section>

            <section id="data-rights"> // Renamed to "Your Data Rights (PIPEDA)"
              <h2>Your Data Rights (PIPEDA)</h2>
              <p>PIPEDA Principles: Individual Access, Accuracy, Challenging Compliance. Detail rights under PIPEDA:</p>
              <ul>
                <li>Right to access your personal information held by us.</li>
                <li>Right to request correction of inaccurate or incomplete information. We will amend the information as required. If we do not agree with the correction request, we will note the disagreement in your file.</li>
                <li>Right to withdraw consent for the collection, use, or disclosure of your personal information (subject to legal or contractual restrictions and reasonable notice).</li>
                <li>Right to challenge our compliance with PIPEDA.</li>
              </ul>
              <p>Explain how users can exercise these rights (e.g., by contacting the Privacy Officer).</p>
            </section>

            <section id="data-security">
              <h2>Security Measures</h2>
              <p>PIPEDA Principle: Safeguards. Describe the security safeguards in place to protect personal information against loss or theft, as well as unauthorized access, disclosure, copying, use, or modification. These safeguards should be appropriate to the sensitivity of the information (e.g., physical, organizational, and technological measures).</p>
            </section>
          */}
          
          <section id="contact"> {/* Renamed to "Contact Us & Accountability" in sections array */}
            <div className="section-heading">
              <FontAwesomeIcon icon={faEnvelope} className="section-icon" />
              <h2>Contact Us & Accountability</h2> {/* PIPEDA Change */}
            </div>
            <p>PIPEDA Principle: Accountability & Challenging Compliance. We have designated a Privacy Officer who is accountable for Our compliance with PIPEDA and this Privacy Policy. If you have any questions about this Privacy Policy, wish to exercise your rights, or have a complaint about our handling of your personal information, You can contact our Privacy Officer:</p> {/* PIPEDA Change */}
            <p><strong>Privacy Officer</strong></p>
            <p>Email: <a href="mailto:admin@originselect.com">admin@originselect.com</a></p>
            
            <p>If you are not satisfied with our response to your complaint, you have the right to lodge a complaint with the Office of the Privacy Commissioner of Canada.</p>
            <p>For general inquiries, you can also reach us at <a href="mailto:admin@originselect.com">admin@originselect.com</a>.</p> {/* Added for consistency and fallback visibility */}
            <a href="mailto:admin@originselect.com" className="contact-button">
              General Inquiries Email Us
            </a>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;